import { getBlackBox, isValidBlackBox } from './tongdunAutoLoad';

const fieldName = 'tongdun_black_box';
/** body 允许使用降级值，取值与校验复用同一策略。 */
const bodyOptions = { allowDegraded: true };
/** 只读检查命中空值后的内部终止标记，不写入请求参数。 */
const emptyField = {};
/**
 * 判断同盾字段是否为空或使用了旧调用方的占位值。
 * @param {*} value 请求体中的同盾字段值
 * @returns {boolean} 是否需要尝试从缓存补值
 */
const isEmptyBlackBox = value => value == null || (typeof value === 'string' && /^(?:1|null|undefined)?$/.test(value.trim()));

/**
 * 访问顶层及 param 内的同盾字段，只有值改变时才重建字符串。
 * @param {*} body 请求体或 param 内容
 * @param {Function} visit 返回替换值的处理函数，也可返回 emptyField 结束只读检查
 * @param {boolean} [nested=false] 是否仅检查 param 当前层
 * @returns {*} 处理后的请求体，或只读检查的 emptyField 终止标记
 */
function visitBody(body, visit, nested = false) {
  if (typeof body === 'string') {
    if (/^\s*\{/.test(body)) {
      if (body.indexOf(fieldName) < 0 && body.indexOf('\\') < 0) {
        return body;
      }
      try {
        let changed = false;
        const value = visitBody(JSON.parse(body), (current) => {
          const next = visit(current);
          changed = changed || next !== current;
          return next;
        }, nested);
        if (value === emptyField) {
          return value;
        }
        return changed ? JSON.stringify(value) : body;
      } catch (error) {
        return body;
      }
    }
    if (!nested) {
      // 仅匹配目标字段及需要解码的键；保留原编码、重复参数和分隔符。
      const fields = /(?:^|&)(tongdun_black_box|param|[^=&]*%[^=&]*)(?:=([^&]*))?(?=&|$)/g;
      let result = '';
      let copied = 0;
      for (let match = fields.exec(body); match; match = fields.exec(body)) {
        try {
          const name = decodeURIComponent(match[1]);
          if (name === fieldName || name === 'param') {
            const value = decodeURIComponent((match[2] || '').replace(/\+/g, ' '));
            const next = name === 'param' ? visitBody(value, visit, true) : visit(value);
            if (next === emptyField) {
              return next;
            }
            if (next !== value) {
              result += `${body.slice(copied, match.index)}${match[0].charAt(0) === '&' ? '&' : ''}${match[1]}=${encodeURIComponent(next)}`;
              copied = fields.lastIndex;
            }
          }
        } catch (error) {
          // 编码异常的字段保持原样，不影响后续字段检查。
        }
      }
      return copied ? result + body.slice(copied) : body;
    }
  }
  const type = Object.prototype.toString.call(body);
  const isForm = !nested && (type === '[object FormData]' || type === '[object URLSearchParams]');
  if (body && (isForm || type === '[object Object]')) {
    for (let index = 0; index < (nested ? 1 : 2); index += 1) {
      const key = index === 0 ? fieldName : 'param';
      if (isForm ? body.has(key) : Object.prototype.hasOwnProperty.call(body, key)) {
        const value = isForm ? body.get(key) : body[key];
        const next = key === 'param' ? visitBody(value, visit, true) : visit(value);
        if (next === emptyField) {
          return next;
        }
        if (next !== value) {
          if (isForm) {
            body.set(key, next);
          } else {
            body[key] = next;
          }
        }
      }
    }
  }
  return body;
}

/**
 * 在实际发送前上报请求体中仍为空的同盾字段。
 * 是否上报以最终请求体为准，SDK 状态用于补充失败原因。
 * @param {Object} config 已完成签名与序列化的 Axios 请求配置
 * @param {Object=} read 当前请求的同盾读取诊断
 * @returns {void}
 */
function reportEmptyBody(config, read) {
  try {
    const { sensors } = window;
    if (!sensors || typeof sensors.track !== 'function' || visitBody(config.data, value => (isEmptyBlackBox(value) ? emptyField : value)) !== emptyField) {
      return;
    }
    const sdk = window.TONGDun || {};
    const time = sdk.time || {};
    const status = (typeof sdk.getStatus === 'function' && sdk.getStatus()) || {};
    const readReason = read && read.reason;
    let reason = readReason || status.sdkReason || status.reason || time.lastReason || '';
    // SDK 原因可保留历史信息；当前请求没有读失败时，不能归因到旧存储异常。
    if (reason === 'td_storage_get_error' && readReason !== reason) {
      reason = '';
    }
    if (!reason || ['td_init_success', 'td_init_degraded_success', 'td_cache_hit'].includes(reason)) {
      reason = status.loading ? 'td_init_pending' : 'td_body_empty';
      if (typeof sdk.getBlackBox !== 'function') {
        reason = 'td_get_blackbox_missing';
      }
    }
    if (typeof sensors.registerPage === 'function') {
      sensors.registerPage({
        lmweb_url: (window.location2 && window.location2.href) || window.location.href,
        lmweb_path: (window.location2 && window.location2.pathname) || window.location.pathname
      });
    }
    const diagnostic = {
      starttime: Date.now(),
      errormsg: `risk_tongdun_empty:${reason}`,
      target: new URL(config.url, new URL(config.baseURL || window.location.href, window.location.href)).pathname,
      errortype: 0,
      pageid: 0,
      webresponsetimes: 1,
      td_fm_loaded: status.fmLoaded || time.fmLoaded ? 1 : 0,
      td_raw_blackbox_len: Number(status.rawBlackBoxLength || time.rawBlackBoxLength || 0),
      td_storage_box_len: Number(status.storageBoxLength || 0),
      td_sdk_reason: status.sdkReason || reason,
      td_fallback_result: Number(status.fallbackResult || time.fallbackResult || 0)
    };
    // 没有实际启动 Web SDK 采集时不填耗时，避免把 App 取值误计为零耗时。
    if (typeof status.attemptElapsedMs === 'number') {
      diagnostic.td_attempt_elapsed_ms = status.attemptElapsedMs;
    }
    sensors.track('kewlweb_onerror', diagnostic);
  } catch (error) {
    // 诊断读取、解析和上报失败都不能中断业务请求。
  }
}

/**
 * 为 POST 请求已声明的同盾空字段补值，并接入发送前的诊断检查。
 * 统一读取有效缓存并允许 body 使用降级值；存储写入失败时由共享内存兜底。
 * 等待由已有请求级配置控制；本方法同步补值，无同盾字段时直接返回。
 * @param {Object} config Axios 请求配置
 * @returns {Object} 补值完成后的同一请求配置
 */
export default function prepareTongdunBody(config) {
  if (String(config.method).toLowerCase() !== 'post') {
    return config;
  }
  let candidate;
  let hasField = false;
  let read;
  try {
    config.data = visitBody(config.data, (value) => {
      hasField = true;
      if (!isEmptyBlackBox(value)) {
        return value;
      }
      if (candidate === undefined) {
        read = {};
        // 补空候选统一经过缓存时效检查，不能从 query 回填已经失效的旧值。
        const blackBox = getBlackBox(false, bodyOptions, read);
        candidate = isValidBlackBox(blackBox, bodyOptions) && !isEmptyBlackBox(blackBox) ? blackBox : '';
      }
      return candidate || value;
    });
  } catch (error) {
    // 参数读取或写入异常时沿用原请求，避免补值逻辑中断业务。
  }
  // 没有声明同盾字段的 body 不增加发送包装和诊断解析。
  if (!hasField) {
    return config;
  }
  // 重试可能复用已包装的适配器，先取回原适配器，避免一次发送重复上报。
  const adapter = config.adapter && (config.adapter.tongdunOriginalAdapter || config.adapter);
  if (typeof adapter === 'function') {
    // 在实际发送时检查，避免 HMAC 预执行转换器产生重复诊断。
    config.adapter = (finalConfig) => {
      reportEmptyBody(finalConfig, read);
      return adapter(finalConfig);
    };
    config.adapter.tongdunOriginalAdapter = adapter;
  }
  return config;
}
